import React, { useEffect } from "react";

import { useMountEffect } from "../hooks/useMountEffect";
import { INumberField } from "../models/formFields";
import { format } from "../utils/format";
import { modulo } from "../utils/math";
import { FormInputFieldWrapper } from "./FormInputFieldWrapper";
import { getFormatter } from "./formatting";
import { useFormField } from "./hooks/useFormField";
import { useFormValidation } from "./hooks/useFormValidation";

export const FormNumber = <T extends string>(
    props: INumberField<T>,
): React.ReactNode => {
    const { type = "number", min, max, step, formatOptions } = props;

    const field = useFormField<T, HTMLInputElement>({
        ...props,
        type: type,
    });

    // Normalize validation to always be an array
    const normalizedValidation = props.validation
        ? Array.isArray(props.validation)
            ? props.validation
            : [props.validation]
        : undefined;

    // Additional validation for number fields
    const validation = useFormValidation({
        name: props.name,
        value: props.value,
        validation: normalizedValidation,
        required: props.required,
        onValidStateChange: props.onValidStateChange,
    });

    const runNumberValidators = (): string[] => {
        const errors = validation.runValidators();
        const floatValue =
            props.value === undefined ? 0 : parseFloat(String(props.value));

        if (!props.value && floatValue !== 0) {
            return errors;
        }

        if ((min || min === 0) && min >= floatValue) {
            errors.push(
                `Please enter an amount equal to or greater than ${format.money(
                    min,
                )}.`,
            );
        }

        if ((max || max === 0) && max < floatValue) {
            errors.push(
                `Please enter a number less than ${format.money(max)}.`,
            );
        }

        if (step && step !== "any") {
            if (modulo(floatValue, step) !== 0) {
                errors.push(
                    `Please enter a number rounded to the closest ${step}.`,
                );
            }
        }

        if (props.onValidStateChange) {
            props.onValidStateChange(props.name, errors);
        }

        return errors;
    };

    // Register cursor tracker on mount for formatting
    useMountEffect(() => {
        if (formatOptions && field.inputRef.current) {
            const formatter = getFormatter(formatOptions);
            formatter.registerCursorTracker(field.inputRef.current);
        }
    });

    // Run number-specific validation on value changes
    useEffect(() => {
        runNumberValidators();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value]);

    const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
        if (!event.currentTarget) {
            return;
        }

        // Format value
        if (formatOptions) {
            const formatter = getFormatter(formatOptions);
            const original = event.currentTarget.value;
            const formatted = formatter.format(original);
            if (original !== formatted) {
                event.currentTarget.value = formatted;
            }
        }
    };

    const inputProps = {
        ...field.inputProps,
        min,
        max,
        step,
        className: props.className ?? "form__field__input",
        onChange: (event: React.FormEvent<HTMLInputElement>) => {
            field.handleChange(event, handleChange);
        },
    };

    return (
        <FormInputFieldWrapper
            {...props}
            type={type}
            focused={field.focused}
            isRequired={field.isRequired}
            onLabelClick={field.onLabelClick}
        >
            <input ref={field.inputRef} {...inputProps} />
        </FormInputFieldWrapper>
    );
};
